import { ReactNode } from 'react';

import { makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import { Card, CardBody, CardHeader, Flex, Link, Text } from '@backstage/ui';

import { useAuth } from '../../../../../behaviors/auth-factory';
import { useSupportMessenger } from '../../../../../hooks/useSupportMessenger';
import { PageLayout } from '../../../../Layout/PageLayout';
import { PageBreadcrumbs } from '../../../PageBreadcrumbs';
import { useOptionalPageBridgeProps } from '../../../utils';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '60vh',
  },
  description: {
    textAlign: 'center',
    maxWidth: '520px',
  },
  cards: {
    maxWidth: '720px',
  },
  link: {
    display: 'inline',
    textDecoration: 'underline',
  },
}));

export type ResourceErrorPageProps = {
  title: string;
  error: ReactNode;
};

// eslint-disable-next-line @spotify-confidence/page-requires-tracking -- rendered inside already-tracked pages via ResourceGuard; tracking happens at the parent
export const ResourceErrorPage = ({ title, error }: ResourceErrorPageProps) => {
  const bridgeProps = useOptionalPageBridgeProps();
  const classes = useStyles();
  const { user } = useAuth();
  const { openChat } = useSupportMessenger();

  const handleContactSupport = () => {
    openChat(
      user?.name && user?.email
        ? { name: user.name, email: user.email }
        : undefined,
    );
  };

  return (
    <PageLayout
      title={null}
      headerBreadcrumbs={<PageBreadcrumbs />}
      narrow={bridgeProps?.narrow}
    >
      <div className={classNames(classes.container)}>
        <Flex justify="center" direction="column" gap="6">
          <Flex direction="column" align="center">
            <Text variant="title-x-small" color="secondary">
              Error
            </Text>
            <Text variant="title-large" as="h2">
              {title}
            </Text>
          </Flex>
          <Flex direction="column" mb="8" align="center">
            <Text variant="body-large" as="p" className={classes.description}>
              {error}
            </Text>
          </Flex>
          <Flex direction="row" gap="6" className={classes.cards}>
            <Card>
              <CardHeader>Refresh the page</CardHeader>
              <CardBody>
                <Text variant="body-medium" color="secondary" as="p">
                  <Link
                    className={classes.link}
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      window.location.reload();
                    }}
                  >
                    Try refreshing
                  </Link>{' '}
                  — the issue may be temporary
                </Text>
              </CardBody>
            </Card>
            <Card>
              <CardHeader>Start fresh</CardHeader>
              <CardBody>
                <Text variant="body-medium" color="secondary" as="p">
                  <Link href="/" className={classes.link}>
                    Go back to the homepage
                  </Link>{' '}
                  and find what you need
                </Text>
              </CardBody>
            </Card>
            <Card>
              <CardHeader>Need help?</CardHeader>
              <CardBody>
                <Text variant="body-medium" color="secondary" as="p">
                  If the problem persists, please{' '}
                  <Link
                    className={classes.link}
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      handleContactSupport();
                    }}
                  >
                    contact our support team
                  </Link>
                </Text>
              </CardBody>
            </Card>
          </Flex>
        </Flex>
      </div>
    </PageLayout>
  );
};
